/* eslint-disable max-lines */
import { gql } from 'urql';

import { IllustrationStyleDictionary } from './IllustrationStyleDictionary';
import { VXLResponsiveTextFragment, VXLTextFragment } from './VxlStyles';

const TagComponent = gql`
  fragment TagComponent on TagComponent {
    alignment
    id
    tag {
      id
      tagColor
      icon
      text
      __typename
    }
  }
`;

//This fragment is different than spacingComponentTileFragment
export const SpacingComponentFragment = gql`
  fragment SpacingComponentFragment on SpacingComponent {
    __typename
    id
    spacing
  }
`;

//This fragment is different than fullWidthTextComponentTileFragment
export const FullWidthTextComponentFragment = gql`
  fragment FullWidthTextComponentFragment on FullWidthTextComponent {
    __typename
    id
    text {
      ...VXLTextFragment
    }
    textAlignment
  }
  ${VXLTextFragment}
`;

export const ChevronVerticalStackComponent = gql`
  fragment ChevronVerticalStackComponent on ChevronVerticalStackComponent {
    id
    children {
      ...FullWidthTextComponentFragment
    }
  }
  ${FullWidthTextComponentFragment}
`;

export const ProgressBarComponent = gql`
  fragment ProgressBarComponent on ProgressBarComponent {
    id
    percentage
    fillStyle {
      ...IllustrationStyleDictionary

      ... on VXLColorType {
        __typename
        color
        id
      }
    }
  }
  ${IllustrationStyleDictionary}
`;

const StyleDictionaryFragment = gql`
  fragment iStyleDictionaryFragment on IStyleDictionary {
    __typename
    ... on IconStyleDictionary {
      __typename
      id
      fallbackURL
      iconKey
      tint
    }
    ...IllustrationStyleDictionary
  }
  ${IllustrationStyleDictionary}
`;

export const VXLTextStackAccessory = gql`
  fragment VXLTextStackAccessory on VXLTextStackAccessory {
    __typename
    id
    icon {
      ...iStyleDictionaryFragment
    }
    title {
      ...FullWidthTextComponentFragment
    }
    subtitle {
      ...FullWidthTextComponentFragment
    }
  }
  ${FullWidthTextComponentFragment}
  ${StyleDictionaryFragment}
`;

export const AnimationFragment = gql`
  fragment animationFragment on AnimationStyleDictionary {
    id
    fallbackURL
    animationKey
  }
`;

export const SpacingComponentTileFragment = gql`
  fragment spacingComponentTileFragment on SpacingComponentTile {
    __typename
    id
    status
    tileType
    tileSpacing: spacing
  }
`;

export const FullWidthTextComponentTileFragment = gql`
  fragment fullWidthTextComponentTileFragment on FullWidthTextComponentTile {
    __typename
    id
    status
    tileType
    text {
      ...VXLTextFragment
    }
    textAlignment
  }
  ${VXLTextFragment}
`;

export const IconFullWidthTextComponent = gql`
  fragment IconFullWidthTextComponent on IconFullWidthTextComponent {
    __typename
    icon {
      ...iStyleDictionaryFragment
    }
    iconBackground
    id
    title {
      ...VXLResponsiveTextFragment
    }
    subtitle {
      ...VXLResponsiveTextFragment
    }
  }
  ${StyleDictionaryFragment}
  ${VXLResponsiveTextFragment}
`;

export const VxlVerticallySplitTile = gql`
  fragment vxlVerticallySplitTileFragment on VXLVerticallySplitTile {
    __typename
    id
    tileType
    status
    showImageTrailing
    image {
      ...iStyleDictionaryFragment
    }
    textStack {
      __typename
      ...fullWidthTextComponentTileFragment
      ...spacingComponentTileFragment
    }
  }
  ${StyleDictionaryFragment}
  ${FullWidthTextComponentTileFragment}
  ${SpacingComponentTileFragment}
`;

export const AnalyticsFragment = gql`
  fragment analytics on AnalyticsEvent {
    name
    properties {
      key
      value
    }
  }
`;

export const VxlButtonFragment = gql`
  fragment vxlButtonFragment on VXLButton {
    __typename
    copy {
      ...VXLTextFragment
    }
    style
    verticalLayout
    horizontalLayout
    onDarkBackground
    isEnabled
  }
  ${VXLTextFragment}
`;

export const LeadingTrailingTextAccessoriesRowComponent = gql`
  fragment LeadingTrailingTextAccessoriesRowComponent on LeadingTrailingTextAccessoriesRowComponent {
    __typename
    id
    leading {
      ...VXLTextStackAccessory
    }
    trailing {
      ...VXLTextStackAccessory
    }
    leadingOnClick {
      ... on PresentBottomSheetAction {
        id
        analyticsEvent {
          ...analytics
        }
      }
    }
    trailingOnClick {
      ... on PresentBottomSheetAction {
        id
        analyticsEvent {
          ...analytics
        }
      }
    }
  }
  ${VXLTextStackAccessory}
  ${AnalyticsFragment}
`;

export const ButtonComponent = gql`
  fragment ButtonComponent on ButtonComponent {
    __typename
    id
    button {
      ...vxlButtonFragment
    }
    onClick {
      __typename
      ... on SDContinueClientFlowAction {
        id
      }
      ... on SDLaunchBelieveOnboardingNavigation {
        id
      }
      ... on SDLaunchBelieveTransferFundsNavigation {
        id
      }
    }
  }
  ${VxlButtonFragment}
`;

export const BulletTextFragment = gql`
  fragment bulletTextFragment on VXLBulletPointCharacter {
    id
    backgroundColor
    characterText {
      ...VXLTextFragment
    }
  }
  ${VXLTextFragment}
`;

export const HeaderAnimationFragment = gql`
  fragment headerAnimation on AnimationHeaderComponent {
    id
    background {
      ...IllustrationStyleDictionary
    }
    animation {
      ...animationFragment
    }
    tearImage {
      ...IllustrationStyleDictionary
    }
  }
  ${AnimationFragment}
  ${IllustrationStyleDictionary}
`;

export const HeaderTextFragment = gql`
  fragment headerText on CopyHeaderComponent {
    id
    background {
      ...IllustrationStyleDictionary
    }
    text {
      ...FullWidthTextComponentFragment
    }
    tearImage {
      ...IllustrationStyleDictionary
    }
  }
  ${FullWidthTextComponentFragment}
  ${IllustrationStyleDictionary}
`;

export const BulletListFragment = gql`
  fragment bulletList on VXLBulletList {
    id
    bulletAlignment
    items {
      id
      text {
        ...VXLTextFragment
      }
      bullet {
        ...bulletTextFragment
      }
    }
  }
  ${VXLTextFragment}
  ${BulletTextFragment}
`;

export const SDDeepLinkActionFragment = gql`
  fragment sdDeepLinkActionFragment on SDDeepLinkAction {
    id
    deeplink
    analyticsEvent {
      ...analytics
    }
  }
  ${AnalyticsFragment}
`;

export const FooterButtonFragment = gql`
  fragment footerButton on ButtonComponent {
    button {
      ...vxlButtonFragment
    }
    onClick {
      ...sdDeepLinkActionFragment
    }
  }
  ${VxlButtonFragment}
  ${SDDeepLinkActionFragment}
`;

export const IconTitleLabelRowTile = gql`
  fragment iconTitleLabelRowTileFragment on IconTitleLabelRowTile {
    __typename
    id
    tileType
    status
    rows {
      __typename
      id
      title {
        ...VXLTextFragment
      }
      label {
        ...VXLTextFragment
      }
      icon {
        ...iStyleDictionaryFragment
      }
    }
  }
  ${VXLTextFragment}
  ${StyleDictionaryFragment}
`;

export const ProductOfferComponentFragment = gql`
  fragment ProductOfferComponentFragment on ProductOfferComponent {
    backgroundColor
    id
    illustration {
      ...IllustrationStyleDictionary
    }
    title {
      ...VXLTextFragment
    }
    offerSubtitle: subtitle {
      ...VXLTextFragment
    }
  }
  ${VXLTextFragment}
`;

export const VxlSeparatedVerticalStackContainer = gql`
  fragment VXLSeparatedVerticalStackContainerFragment on VXLSeparatedVerticalStackContainer {
    __typename
    id
    items {
      __typename
      id
      content {
        __typename
        ...ButtonComponent
        ...FullWidthTextComponentFragment
        ...SpacingComponentFragment
        ... on HtmlComponent {
          html
          id
          boldStyle {
            fontStyle
            color
          }
          linkStyle {
            fontStyle
            color
          }
          textStyle {
            fontStyle
            color
          }
        }
        ... on SDProductStatusRowComponent {
          __typename
          title {
            ...VXLTextFragment
          }
          icon {
            ...iStyleDictionaryFragment
          }
          accessory {
            ... on Tag {
              id
              tagColor
              text
              icon
            }
            ...VXLTextFragment
          }
        }
      }
      onClick {
        __typename

        ... on PresentBottomSheetAction {
          id
          analyticsEvent {
            ...analyticsEventFragment
          }
        }
        ... on SDLaunchVLOCNavigation {
          id
          analytics {
            ...analytics
          }
        }
        ... on SDLaunchAdvanceOnboardingNavigation {
          id
          analytics {
            ...analytics
          }
        }
        ... on SDLaunchAdvanceAccountNavigation {
          id
          analytics {
            ...analytics
          }
        }
      }
    }
  }
  ${ButtonComponent}
  ${SpacingComponentFragment}
  ${FullWidthTextComponentFragment}
  ${VXLTextFragment}
  ${StyleDictionaryFragment}
  ${AnalyticsFragment}
`;

export const VxlVerticalSegmentedCardContainerChild = gql`
  fragment VXLVerticalSegmentedCardContainerChild on VXLVerticalSegmentedCardContainerChild {
    id
    content {
      __typename
      ...ChevronVerticalStackComponent
      ...SpacingComponentFragment
      ...ProgressBarComponent
      ...LeadingTrailingTextAccessoriesRowComponent
      ...IconFullWidthTextComponent
      ...ButtonComponent
      ...ProductOfferComponentFragment
    }
    onClick {
      __typename
      ... on SDLaunchVLOCNavigation {
        id
        analytics {
          ...analytics
        }
      }
      ... on SDLaunchDMGMNavigation {
        id
        analytics {
          ...analytics
        }
      }
      ... on SDLaunchAdvanceOnboardingNavigation {
        id
        analytics {
          ...analytics
        }
      }
      ... on SDLaunchAdvanceAccountNavigation {
        id
        analytics {
          ...analytics
        }
      }
    }
  }
  ${ChevronVerticalStackComponent}
  ${SpacingComponentFragment}
  ${ProgressBarComponent}
  ${LeadingTrailingTextAccessoriesRowComponent}
  ${IconFullWidthTextComponent}
  ${ButtonComponent}
  ${ProductOfferComponentFragment}
  ${AnalyticsFragment}
`;

export const VxlCardContainerChild = gql`
  fragment VXLCardContainerChildResult on VXLCardContainerChildResult {
    __typename
    ...ChevronVerticalStackComponent
    ...SpacingComponentFragment
    ...ProgressBarComponent
    ...LeadingTrailingTextAccessoriesRowComponent
    ...IconFullWidthTextComponent
    ...FullWidthTextComponentFragment
    ...TagComponent
    ...ButtonComponent
    ...ProductOfferComponentFragment
  }
  ${ChevronVerticalStackComponent}
  ${SpacingComponentFragment}
  ${ProgressBarComponent}
  ${LeadingTrailingTextAccessoriesRowComponent}
  ${IconFullWidthTextComponent}
  ${FullWidthTextComponentFragment}
  ${ButtonComponent}
  ${TagComponent}
  ${ProductOfferComponentFragment}
`;

export const VxlCardContainer = gql`
  fragment VXLCardContainerFragment on VXLCardContainer {
    id
    content {
      ...VXLCardContainerChildResult
    }
    style {
      __typename
      ... on VXLStaticStyle {
        id
        staticBorderColor: borderColor
      }
      ... on VXLNavigableStyle {
        id
        borderColor
      }
      ... on VXLSelectableStyle {
        id
      }
    }
    cardContainerOnClick: onClick {
      __typename
      ... on SDLaunchVLOCNavigation {
        id
        analytics {
          ...analytics
        }
      }
      ... on SDLaunchDMGMNavigation {
        id
        analytics {
          ...analytics
        }
      }
      ... on SDLaunchAdvanceOnboardingNavigation {
        id
        analytics {
          ...analytics
        }
      }
      ... on SDLaunchAdvanceAccountNavigation {
        id
        analytics {
          ...analytics
        }
      }
    }
  }
  ${VxlCardContainerChild}
  ${AnalyticsFragment}
`;

export const VxlPageSectionVerticalStackContainer = gql`
  fragment VXLPageSectionVerticalStackContainerFragment on VXLPageSectionVerticalStackContainer {
    __typename
    id
    content {
      __typename
      ...ProgressBarComponent
      ...FullWidthTextComponentFragment
      ...SpacingComponentFragment
      ...ButtonComponent
      ...LeadingTrailingTextAccessoriesRowComponent
      ...VXLSeparatedVerticalStackContainerFragment
    }
  }
  ${ProgressBarComponent}
  ${FullWidthTextComponentFragment}
  ${SpacingComponentFragment}
  ${ButtonComponent}
  ${LeadingTrailingTextAccessoriesRowComponent}
  ${VxlSeparatedVerticalStackContainer}
`;

export const VxlVerticalSegmentedCardContainer = gql`
  fragment VXLVerticalSegmentedCardContainer on VXLVerticalSegmentedCardContainer {
    id
    children {
      __typename
      ...VXLVerticalSegmentedCardContainerChild
    }
  }
  ${VxlVerticalSegmentedCardContainerChild}
`;

export const VXLIconTypeFragment = gql`
  fragment VXLIconTypeFragment on VXLIconType {
    __typename
    iconKey: key
  }
`;

export const VxlListItemFragment = gql`
  fragment VxlListItemFragment on VxlListItem {
    primaryText
    secondaryText
    primaryNumberOfLines
    lineFormat
    isDisabled
    showsChevron
    leadingAccessory {
      __typename
      ...VXLIconTypeFragment
      ... on UserAvatar {
        userAvatarAsset {
          __typename
          ... on CopyValue {
            text
          }
          ... on URLType {
            url
          }
        }
      }
      ... on VXLIllustrationType {
        __typename
        key
      }
    }
    trailingAccessory {
      __typename
      ... on Tag {
        id
        tagColor
        text
        icon
      }
      ... on Badge {
        color
        text
        __typename
      }
    }
    onPressAction {
      ... on RouteNavigationAction {
        __typename
        route
        analyticsEvent {
          ...analytics
        }
      }
      ... on PresentClientBottomSheetAction {
        id
        __typename
        analyticsEvent {
          ...analytics
        }
      }
      ... on LaunchPinwheelFlowAction {
        __typename
        id
        shouldLaunchPinwheelSdk
        analyticsEvent {
          ...analytics
        }
      }
    }
  }
  ${VXLIconTypeFragment}
  ${AnalyticsFragment}
`;

export const VxlSectionDividerFragment = gql`
  fragment VxlSectionDividerFragment on VxlSectionDivider {
    labelText
  }
`;

export const MyVaroVxlTileFragment = gql`
  fragment MyVaroVxlTileFragment on MyVaroVxlTile {
    content {
      __typename
      ...VxlListItemFragment
      ...VxlSectionDividerFragment
    }
  }
  ${VxlListItemFragment}
  ${VxlSectionDividerFragment}
`;

export const MyVaroSectionTileFragment = gql`
  fragment MyVaroSectionTileFragment on MyVaroSectionTile {
    __typename
    header {
      __typename
      id
      status
      tileType
      ...MyVaroVxlTileFragment
    }
    tiles {
      __typename
      id
      status
      tileType
      ...MyVaroVxlTileFragment
    }
  }
  ${MyVaroVxlTileFragment}
  ${AnalyticsFragment}
`;
